import { mapActions, mapGetters, mapMutations } from 'vuex';
import Vue from 'vue';
import { Carousel, Slide } from 'vue-carousel';

//---helpers
import { BLOG_NEWS_TYPE, decodeHtmlEntities } from '@/helpers';
import { globalLoader, MEDIA } from '@/helpers/variables';
import { storageSession } from '@/utils/storage';

//---mixins
import { dynamicHead } from '@/mixins/dynamic-head.js';

//---components
import Banner from '@/components/custom/Banner/index.vue';
import ProductCard from '@/components/cards/ProductCard/index.vue';
import SeriesCard from '@/components/cards/SeriesCard/index.vue';
import ContentBlockDescription from '@/components/custom/ContentBlock/ContentBlockDescription/index.vue';
import IconArrowPrevLight from '@/components/icons/IconArrowPrevLight.vue';
import IconArrowNextLight from '@/components/icons/IconArrowNextLight.vue';
import IconPlay from '@/components/icons/IconPlay.vue';
import InstaBlock from '@/components/custom/InstaBlock/index.vue';
import HomeVideo from '@/modules/Home/HomeVideo/index.vue';
import HomeCosmetologist from '@/modules/Home/HomeCosmetologist/index.vue';
import NewsCard from '@/components/cards/NewsCard/index.vue';
import HomeFeedbacks from './HomeFeedbacks/index.vue';
import HomeRecommendations from '@/modules/Home/HomeRecommendations/index.vue';
import HomeSubscribe from '@/modules/Home/HomeSubscribe/index.vue';

import PushBlock from '@/components/custom/PushBlock/index.vue';

export default Vue.extend({
  name: 'Home',
  mixins: [dynamicHead],
  components: {
    PushBlock,
    HomeSubscribe,
    HomeRecommendations,
    HomeCosmetologist,
    HomeVideo,
    InstaBlock,
    ContentBlockDescription,
    IconPlay,
    IconArrowNextLight,
    IconArrowPrevLight,
    Banner,
    ProductCard,
    Carousel,
    Slide,
    SeriesCard,
    NewsCard,
    HomeFeedbacks
  },
  data() {
    return {
      BLOG_NEWS_TYPE,
      homePage: null,
      homeBanners: [],
      homeSeries: [],
      homeNew: [],
      homeTop: [],
      homeNews: [],
      homeInstagramPosts: [],
      homeFeedbacks: []
    };
  },
  created() {
    // window.addEventListener('scroll', this.videoScroll);
    this.showPopups();
    this._loadContent();
    this._checkReviewQuery();
  },
  mounted() {},
  watch: {
    user: {
      immediate: true,
      handler(newVal) {
        if (
          newVal &&
          newVal.ordersCount === 0 &&
          !storageSession.getItem('firstOrderPromo') &&
          this.isFirstOrderPopupVisible
        ) {
          setTimeout(() => {
            this.modalOpen('firstOrderPromo');
          }, 7000);
        }
      }
    },
    isShowFirstOrderPopup: {
      immediate: true,
      handler(val) {
        if (val) {
          setTimeout(() => {
            this.modalOpen('firstOrderPromo');
          }, 7000);
        }
      }
    }
  },
  computed: {
    ...mapGetters({
      isAuthenticated: 'auth/isAuthenticated',
      user: 'profile/user',
      favourites: 'favorites/whichList',
      headerMenu: 'setting/headerMenu',
      contacts: 'setting/variables',
      appSize: 'app/size',
      modal: 'app/modal',
      modalData: 'app/modalData',
      variables: 'setting/variables'
    }),
    isValentinesDayRange() {
      let now = new Date();
      let startDate = new Date(2026, 1, 9, 9, 0, 0);
      let endDate = new Date(2026, 1, 14, 23, 59, 59);
      return now >= startDate && now <= endDate;
    },
    isShowFirstOrderPopup() {
      if (this.isValentinesDayRange) return false;

      let apiStatus = this.variables?.orders?.isFirstOrderPopupVisible;
      if (!apiStatus) return false;

      if (storageSession.getItem('firstOrderPromo')) return false;

      return !this.isAuthenticated || this.user?.ordersCount === 0;
    },
    seminarsData() {
      return this.homePage?.pageContents?.data?.length ? this.homePage.pageContents.data[0] : null;
    },
    homeRecommendations() {
      return this.homePage?.pageRecommendations?.data?.length ? this.homePage.pageRecommendations.data : [];
    },
    topSellerProducts() {
      if (this.isAuthenticated) {
        if (!this.homeTop) return [];
        if (!this.favourites) return [];
        return this.homeTop.map((e) => {
          const item = this.favourites.find((t) => t.productId === e.id);
          e.selected = !!item;
          return e;
        });
      } else {
        return this.homeTop;
      }
    },

    isShowTopSellerProductsSliderArrows() {
      let itemsLength = this.topSellerProducts.length;
      let status = itemsLength > 4;
      if (itemsLength > 2 && this.appSize.w <= MEDIA.M768) {
        status = true;
      } else if (itemsLength > 3 && this.appSize.w <= MEDIA.M1024) {
        status = true;
      }
      return status;
    },

    newProducts() {
      if (this.isAuthenticated) {
        if (!this.homeNew) return [];
        if (!this.favourites) return [];
        return this.homeNew.map((e) => {
          const item = this.favourites.find((t) => t.productId === e.id);
          e.selected = !!item;
          return e;
        });
      } else {
        return this.homeNew;
      }
    },

    isShowNewProductsSliderArrows() {
      let itemsLength = this.newProducts.length;
      let status = itemsLength > 4;
      if (itemsLength > 2 && this.appSize.w <= MEDIA.M768) {
        status = true;
      } else if (itemsLength > 3 && this.appSize.w <= MEDIA.M1024) {
        status = true;
      }
      return status;
    }
  },
  methods: {
    ...mapMutations({
      changePaymentInfoPopup: 'popups/CHANGE_PAYMENT_INFO_POPUP',
      resetBasket: 'basket/RESET_BASKET',
      changeLoginPopup: 'popups/CHANGE_LOGIN_POPUP',
      changeSuccessPayment: 'popups/SHOW_SUCCESS_PAYMENT_POPUP',
      modalOpen: 'app/MODAL_OPEN',
      modalClose: 'app/MODAL_CLOSE',
      modalSetData: 'app/MODAL_SET_DATA'
    }),
    ...mapActions({
      getHomePage: 'home/GET_HOME_PAGE',
      getHomeBanners: 'home/GET_HOME_BANNERS',
      getHomeSeries: 'home/GET_HOME_SERIES',
      getHomeNew: 'home/GET_HOME_NEW',
      getHomeTop: 'home/GET_HOME_TOP',
      getHomeNews: 'home/GET_HOME_NEWS',
      getHomeInstagramPosts: 'home/GET_HOME_INSTAGRAM_POSTS',
      getProductFeedbacks: 'home/GET_PRODUCT_FEEDBACKS',
      getProducts: 'products/GET_PRODUCTS'
    }),
    async _checkReviewQuery() {
      if (!this.isAuthenticated) return;
      let { review } = this.$route.query;
      if (!review) return;

      let reviewData = review.split(';').reduce((acc, item) => {
        let splitData = item.split(':');
        let key = splitData[0];
        let value = key === 'ids' ? splitData[1].split(',') : splitData[1];
        return { ...acc, [key]: value };
      }, {});

      let products = await this.getProducts({ ids: reviewData.ids });

      this.modalSetData({
        key: 'reviewModal',
        data: {
          products,
          email: reviewData.email,
          name: reviewData.name
        }
      });
      this.modalOpen('reviewModal');
    },

    async _loadContent() {
      globalLoader(true);

      await Promise.all([
        this.getHomePage().then((result) => (this.homePage = result)),
        this.getProductFeedbacks().then((result) => (this.homeFeedbacks = result)),
        this.getHomeBanners().then((result) => (this.homeBanners = result)),
        this.getHomeSeries().then((result) => (this.homeSeries = result)),
        this.getHomeNew().then((result) => (this.homeNew = result)),
        this.getHomeTop().then((result) => (this.homeTop = result)),
        this.getHomeNews().then((result) => (this.homeNews = result)),
        this.getHomeInstagramPosts().then((result) => (this.homeInstagramPosts = result))
      ]);

      this._setMetaParams();
      this.setMeta();

      globalLoader(false);
    },
    _setMetaParams() {
      console.log('_setMetaParams', this.homePage);
      let { metaTitle, metaDescription, metaImage } = this.homePage;
      this.metaParams.title = metaTitle || this.$t('seo.metaTitleDefault');
      this.metaParams.description = metaDescription
        ? decodeHtmlEntities(metaDescription)
        : this.$t('seo.metaDescDefault');
      this.metaParams.image = metaImage || null;
    },
    showPopups() {
      let { query } = this.$route;
      if (query.hasOwnProperty('email') && query.hasOwnProperty('token')) {
        this.changeLoginPopup(false);
        this.modalOpen('restore');
      }

      if (!this.isAuthenticated) {
        if (query.hasOwnProperty('email') && !query.hasOwnProperty('token')) {
          this.modalClose('restore');
          this.changeLoginPopup(true);
        }
        if (!query.hasOwnProperty('email') && !query.hasOwnProperty('token')) {
          this.modalClose('restore');
          // this.changeLoginPopup(true)
        }
      }

      if (query.hasOwnProperty('payment-popup') && query.hasOwnProperty('label.status') && query.status === '1') {
        this.changeSuccessPayment(true, {
          notCall: Number.parseInt(query['need-confirm'] || 0) === 1
        });
      }

      if (query.hasOwnProperty('payment')) {
        this.changePaymentInfoPopup(true);
        if (query.payment === 'success') {
          this.resetBasket();
        } else {
          this.$router.push({ name: 'buy-flow-order' }).catch(() => {});
        }
      }
    },

    onClickTopNav(direction) {
      const carousel = this.$refs['homeTopRef'];
      carousel.advancePage(direction);
    },
    onClickNewNav(direction) {
      const carousel = this.$refs['homeNewRef'];
      carousel.advancePage(direction);
    }
  }
});
